import { omit } from 'lodash';
import { ZodError, z } from 'zod';
import type { MessageFields } from '@/data/message/model';
import { RequestSchema } from '@/data/request/model';
import type { UserFields } from '@/data/user/model';
import Monolith from '@/shared/api/monolith';
import { parseAPIError } from '@/shared/redux/helpers/parseApiError';
import { createWiwAsyncThunk } from '@/store';

type FetchRequestsArgs = {
  page?: number;
  clearStore?: boolean;
  [key: string]: any;
};

const defaultParams = {
  page: 0,
  clearStore: false,
};

export const fetchRequests = createWiwAsyncThunk<z.infer<typeof FetchRequestsResponse>, FetchRequestsArgs>(
  'request/fetch',
  async (params = defaultParams, { dispatch, rejectWithValue }) => {
    params = { ...defaultParams, ...params };
    try {
      const response = await Monolith.url('/requests')
        .query(omit(params, ['clearStore']))
        .get();

      const parsedResponse = FetchRequestsResponse.parse(response);
      if (parsedResponse.more) {
        dispatch(fetchRequests({ ...params, page: (params.page ?? 0) + 1, clearStore: false }));
      }
      return parsedResponse;
    } catch (error) {
      if (error instanceof ZodError) {
        return rejectWithValue(error);
      }

      return rejectWithValue(parseAPIError(error));
    }
  },
);

const FetchRequestsResponse = z.object({
  requests: z.array(RequestSchema),
  // TODO: Add a schema for messages so we actually have zod validation on messages
  messages: z.array(z.custom<MessageFields>()),
  more: z.boolean(),
  page: z.number(),
  total: z.number(),
});

type FetchRequestArgs = number;

export const fetchRequest = createWiwAsyncThunk<z.infer<typeof FetchRequestResponse>, FetchRequestArgs>(
  'request/fetchSingle',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await Monolith.url(`/requests/${requestId}`).get();
      return FetchRequestResponse.parse(response);
    } catch (error) {
      if (error instanceof ZodError) {
        return rejectWithValue(error);
      }
      return rejectWithValue(parseAPIError(error));
    }
  },
);

const FetchRequestResponse = z.object({
  request: RequestSchema,
  // TODO: Add a schema for users so we actually have zod validation on users
  users: z.array(z.custom<UserFields>()).optional(),
  // TODO: Add a schema for messages so we actually have zod validation on messages
  messages: z.array(z.custom<MessageFields>()).optional(),
});
